import React, { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Modal from 'Components/UI/Modal';
import Button from 'Components/UI/Button';
import SpinCustom from 'Components/UI/SpinCustom';
import {
  currentStepSelector,
  mappingConfigsSelector,
  selectedFileSelector,
  fileAnalysisResultSelector,
  importSettingsSelector,
  errorSelector,
  isLoadingSelector,
  isSavingSelector,
  importProgressSelector,
  importResultSelector,
  BcfImportActions,
} from 'Store/BcfImport';
import { BcfImportStep } from 'Store/BcfImport/interfaces/IBcfImportState';
import { BcfImportApi } from 'Data/BcfImport.api';
import {
  StepIndicator,
  Step1FileUpload,
  Step2DataMapping,
  Step3Settings,
  Step4Summary,
  Step5Execute,
} from './components';
import {
  ContentArea,
  ErrorBanner,
  FooterButtons,
  FooterDivider,
  LayoutContainer,
  LoadingWrapper,
} from './styles';

interface IBcfImportModalProps {
  visible: boolean;
  constructionSiteId: string | number;
  onClose: () => void;
  onImportSuccess?: (result: any) => void;
}

const BcfImportModal: FC<IBcfImportModalProps> = ({
  visible,
  constructionSiteId,
  onClose,
  onImportSuccess,
}) => {
  const { t } = useTranslation('activities');
  const dispatch = useDispatch();
  const [availableDefaults, setAvailableDefaults] = useState<any>(null);
  const [isLoadingDefaults, setIsLoadingDefaults] = useState(false);
  const [defaultsError, setDefaultsError] = useState<string | undefined>(undefined);

  const currentStep = useSelector(currentStepSelector);
  const selectedFile = useSelector(selectedFileSelector);
  const fileAnalysisResult = useSelector(fileAnalysisResultSelector);
  const mappingConfigs = useSelector(mappingConfigsSelector);
  const importSettings = useSelector(importSettingsSelector);
  const error = useSelector(errorSelector);
  const isLoading = useSelector(isLoadingSelector);
  const isSaving = useSelector(isSavingSelector);
  const importProgress = useSelector(importProgressSelector);
  const importResult = useSelector(importResultSelector);

  const stepLabels = useMemo(() => [
    t('bcfImportModal.stepFileUpload'),
    t('bcfImportModal.stepDataMapping'),
    t('bcfImportModal.stepSettings'),
    t('bcfImportModal.stepSummary'),
    t('bcfImportModal.stepExecute'),
  ], [t]);

  const fetchDefaultsData = async () => {
    setIsLoadingDefaults(true);
    setDefaultsError(undefined);
    try {
      const response = await BcfImportApi.fetchDefaults(constructionSiteId);
      setAvailableDefaults(response.data.data);
    } catch (err: any) {
      console.error('Failed to fetch defaults', err);
      setDefaultsError(err?.message || 'Falha ao carregar dados. Verifique sua conexão e tente novamente.');
    } finally {
      setIsLoadingDefaults(false);
    }
  };

  useEffect(() => {
    if (visible && currentStep === BcfImportStep.DATA_MAPPING && !availableDefaults && !isLoadingDefaults) {
      fetchDefaultsData();
    }
  }, [visible, currentStep, availableDefaults, constructionSiteId]);

  useEffect(() => {
    if (!visible) {
      setTimeout(() => {
        dispatch(BcfImportActions.resetImportState());
        setAvailableDefaults(null);
        setDefaultsError(undefined);
      }, 300);
    }
  }, [visible, dispatch]);

  const handleFileSelect = (file: File) => {
    dispatch(BcfImportActions.uploadFile(file));
    dispatch(BcfImportActions.analyzeFile(file));
  };

  const handleMappingChange = (fieldType: string, bcfValue: string, coordlyValue: string | number) => {
    const currentMapping = mappingConfigs[fieldType as keyof typeof mappingConfigs];
    const updatedMapping = {
      ...currentMapping,
      mappingRules: {
        ...currentMapping.mappingRules,
        [bcfValue]: coordlyValue,
      },
    };
    dispatch(BcfImportActions.updateFieldMapping(fieldType, updatedMapping));
  };

  const handleDefaultValueChange = (fieldType: string, defaultValue: string | number) => {
    dispatch(BcfImportActions.updateImportSettings({
      [`default${fieldType.charAt(0).toUpperCase() + fieldType.slice(1)}`]: defaultValue,
    }));
  };

  const handleIgnoreChange = (fieldType: string, isIgnored: boolean) => {
    const currentMapping = mappingConfigs[fieldType as keyof typeof mappingConfigs];
    const updatedMapping = {
      ...currentMapping,
      isIgnored,
    };
    dispatch(BcfImportActions.updateFieldMapping(fieldType, updatedMapping));
  };

  const handleSettingsChange = (settings: any) => {
    dispatch(BcfImportActions.updateImportSettings(settings));
  };

  const handleNext = async () => {
    dispatch(BcfImportActions.validateAndAdvance(currentStep));
  };

  const handleImport = () => {
    dispatch(BcfImportActions.executeImport(constructionSiteId));
  };

  const handleBack = () => {
    dispatch(BcfImportActions.goBack());
  };

  const handleFinish = () => {
    onImportSuccess?.(importResult);
    onClose();
  };

  const isLastStep = currentStep === BcfImportStep.EXECUTE;
  const isCompleted = importResult?.success;

  const footerButtons = useMemo(() => {
    const buttons: React.ReactNode[] = [];

    buttons.push(
      <Button key="cancel" type="text" onClick={onClose}>
        {t('bcfImportModal.cancel')}
      </Button>
    );

    if (currentStep > BcfImportStep.FILE_UPLOAD) {
      buttons.push(
        <Button key="back" onClick={handleBack} disabled={isSaving || isLoading}>
          {t('bcfImportModal.back')}
        </Button>
      );
    }

    if (currentStep < BcfImportStep.EXECUTE) {
      buttons.push(
        <Button
          key="next"
          type="primary"
          onClick={handleNext}
          loading={isLoading}
          disabled={isSaving || isCompleted}
        >
          {t('bcfImportModal.next')}
        </Button>
      );
    }

    if (currentStep === BcfImportStep.EXECUTE) {
      buttons.push(
        <Button
          key="import"
          type="primary"
          onClick={handleImport}
          loading={isSaving}
          disabled={!selectedFile || isCompleted}
        >
          {isCompleted ? t('bcfImportModal.importCompleted') : t('bcfImportModal.import')}
        </Button>
      );
      if (isCompleted) {
        buttons.push(
          <Button key="finish" type="primary" onClick={handleFinish}>
            {t('bcfImportModal.finish')}
          </Button>
        );
      }
    }

    return (
      <FooterButtons>
        {buttons}
      </FooterButtons>
    );
  }, [currentStep, isLoading, isSaving, isCompleted, selectedFile, onClose, handleBack, handleNext, handleImport, handleFinish, t]);

  const renderStepContent = () => {
    switch (currentStep) {
      case BcfImportStep.FILE_UPLOAD:
        return (
          <Step1FileUpload
            selectedFile={selectedFile}
            analysisResult={fileAnalysisResult}
            isLoading={isLoading}
            error={error}
            onFileSelect={handleFileSelect}
          />
        );
      case BcfImportStep.DATA_MAPPING:
        return (
          <Step2DataMapping
            mappingConfigs={mappingConfigs}
            analysisResult={fileAnalysisResult}
            isLoading={isLoading}
            isLoadingDefaults={isLoadingDefaults}
            defaultsError={defaultsError}
            error={error}
            availableDefaults={availableDefaults}
            onMappingChange={handleMappingChange}
            onDefaultValueChange={handleDefaultValueChange}
            onRetryLoadDefaults={fetchDefaultsData}
            onIgnoreChange={handleIgnoreChange}
          />
        );
      case BcfImportStep.SETTINGS:
        return (
          <Step3Settings
            settings={importSettings}
            isLoading={isLoading}
            isLoadingDefaults={isLoadingDefaults}
            defaultsError={defaultsError}
            error={error}
            availableDefaults={availableDefaults}
            onSettingsChange={handleSettingsChange}
            onRetryLoadDefaults={fetchDefaultsData}
          />
        );
      case BcfImportStep.SUMMARY:
        return (
          <Step4Summary
            fileAnalysisResult={fileAnalysisResult}
            mappingConfigs={mappingConfigs}
            importSettings={importSettings}
            isLoading={isLoading}
            error={error}
          />
        );
      case BcfImportStep.EXECUTE:
        return (
          <Step5Execute
            importProgress={importProgress}
            isImporting={isSaving}
            importResult={importResult}
            error={error}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      width={900}
      title={t('bcfImportModal.title')}
      newHeader
      footerButtons={footerButtons}
      footerActive={false}
      maskClosable={false}
    >
      <LayoutContainer>
        <StepIndicator
          currentStep={currentStep}
          totalSteps={stepLabels.length}
          stepLabels={stepLabels}
        />
        <ContentArea>
          {error && currentStep !== BcfImportStep.EXECUTE && (
            <ErrorBanner>{error}</ErrorBanner>
          )}
          {isLoading && currentStep !== BcfImportStep.DATA_MAPPING ? (
            <LoadingWrapper>
              <SpinCustom size={32} />
            </LoadingWrapper>
          ) : (
            renderStepContent()
          )}
        </ContentArea>
      </LayoutContainer>
      <FooterDivider />
    </Modal>
  );
};

export default BcfImportModal;
